import { cashbookServices } from "@/modules/finances/cashbooks/services/cashbook.services"
import { CashBookDetailDto, CashBookStatus, MAP_CASHBOOK_STATUS } from "@/modules/finances/cashbooks/types"
import { deleteReceipt } from "@/modules/finances/receipts/services/receipts.services"
import { Receipt } from "@/modules/finances/receipts/types"
import { formatCurrency } from "@/utils/convert"
import { formatterInputNumber } from "@/utils/helpers"
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, LockOutlined, PlusOutlined, PrinterOutlined } from "@ant-design/icons"
import { Button, Card, Col, Descriptions, Flex, Form, Input, InputNumber, message, Modal, Row, Space, Statistic, Switch, Table, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import ReceiptForm from "./ReceiptForm"

const { Title, Text } = Typography

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [cashBookData, setCashBookData] = useState<CashBookDetailDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [closeModalVisible, setCloseModalVisible] = useState(false)
  const [reopenModalVisible, setReopenModalVisible] = useState(false)
  const [closeForm] = Form.useForm()
  const [showSeparatedView, setShowSeparatedView] = useState(false)
  const [receiptFormVisible, setReceiptFormVisible] = useState(false)
  const [receiptFormMode, setReceiptFormMode] = useState<'create' | 'update'>('create')
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null)
  const [deleteReceiptModalVisible, setDeleteReceiptModalVisible] = useState(false)
  const [receiptToDelete, setReceiptToDelete] = useState<Receipt | null>(null)

  const fetchCashBookData = async (cashBookId: string) => {
    setLoading(true)
    try {
      const response = await cashbookServices.getCashBookById(cashBookId)
      if (response?.data) {
        setCashBookData(response.data)
      } else {
        setCashBookData(null)
      }
    } catch (error) {
      console.error('Error fetching cash book data:', error)
      setCashBookData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      fetchCashBookData(id)
    }
  }, [id])

  const handleCloseCashBook = async (values: any) => {
    try {
      const data = {
        closing_balance: values.closing_balance,
        notes: values.notes
      }
      const response = await cashbookServices.closeCashBook(id!, data)
      if (response?.data) {
        message.success('Chốt sổ quỹ thành công!')
        setCloseModalVisible(false)
        closeForm.resetFields()
        // Refresh data
        if (id) {
          fetchCashBookData(id)
        }
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi chốt sổ quỹ!')
    }
  }

  const handleCloseCashBookClick = () => {
    setCloseModalVisible(true)
  }

  const handleReopenCashBook = async () => {
    try {
      const response = await cashbookServices.reopenCashBook(id!)
      if (response?.data) {
        message.success('Hủy chốt sổ quỹ thành công!')
        setReopenModalVisible(false)
        // Refresh data
        if (id) {
          fetchCashBookData(id)
        }
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi hủy chốt sổ quỹ!')
    }
  }

  const handleReopenCashBookClick = () => {
    setReopenModalVisible(true)
  }

  const handleBack = () => {
    navigate('/finances/cashbooks')
  }

  const showPrintView = () => {
    navigate(`/finances/cashbooks/${id}/print`)
  }

  const handleAddReceiptClick = () => {
    setReceiptFormMode('create')
    setSelectedReceipt(null)
    setReceiptFormVisible(true)
  }

  const handleEditReceiptClick = (receipt: Receipt) => {
    setReceiptFormMode('update')
    setSelectedReceipt(receipt)
    setReceiptFormVisible(true)
  }

  const handleDeleteReceiptClick = (receipt: Receipt) => {
    setReceiptToDelete(receipt)
    setDeleteReceiptModalVisible(true)
  }

  const handleDeleteReceipt = async () => {
    if (!receiptToDelete?.id) return
    
    try {
      await deleteReceipt(receiptToDelete)
      message.success('Xóa thu chi thành công!')
      setDeleteReceiptModalVisible(false)
      setReceiptToDelete(null)
      // Refresh data
      if (id) {
        fetchCashBookData(id)
      }
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa thu chi!')
    }
  }

  const handleReceiptFormSubmit = () => {
    setReceiptFormVisible(false)
    // Refresh data
    if (id) {
      fetchCashBookData(id)
    }
  }

  const transactionColumns = [
    {
      title: 'STT',
      dataIndex: 'index',
      key: 'index',
      width: '5%',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Thời gian',
      dataIndex: 'date_happened',
      key: 'date_happened',
      width: '12%',
      render: (date_happened: string) => date_happened ? dayjs(date_happened).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Tên giao dịch',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: '15%',
      render: (amount: number, record: any) => (
        <span style={{ color: record.type === 'income' ? '#52c41a' : '#ff4d4f' }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Người tạo',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: '12%',
      render: (created_by_name: any) => created_by_name || '-',
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name',
      key: 'category_name',
      width: '12%',
      render: (category_name: any) => category_name || '-',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: '15%',
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: '10%',
      render: (_: any, record: Receipt) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditReceiptClick(record)}
            disabled={cashBookData?.status === CashBookStatus.CLOSED}
          >
            Sửa
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteReceiptClick(record)}
            disabled={cashBookData?.status === CashBookStatus.CLOSED}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ]

  // Phân tách receipts thành thu và chi
  const incomeReceipts = cashBookData?.receipts?.filter(receipt => receipt.type === 'income') || []
  const expenseReceipts = cashBookData?.receipts?.filter(receipt => receipt.type === 'expense') || []

  // Columns cho bảng riêng (không có cột Loại)
  const separatedColumns = [
    {
      title: 'STT',
      key: 'index',
      width: '5%',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Thời gian',
      dataIndex: 'date_happened',
      key: 'date_happened',
      width: '12%',
      render: (date_happened: string) => date_happened ? dayjs(date_happened).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Tên giao dịch',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: '12%',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Người tạo',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: '12%',
      render: (created_by_name: any) => created_by_name || '-',
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name',
      key: 'category_name',
      width: '12%',
      render: (category_name: any) => category_name || '-',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: '12%',
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: '15%',
      render: (_: any, record: Receipt) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditReceiptClick(record)}
            disabled={cashBookData?.status === CashBookStatus.CLOSED}
          >
            Sửa
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteReceiptClick(record)}
            disabled={cashBookData?.status === CashBookStatus.CLOSED}
          >
            Xóa
          </Button>
        </Space>
      ),
    },
  ]

  if (loading) {
    return <div>Loading...</div>
  }

  if (!cashBookData) {
    return <div>Không tìm thấy sổ quỹ</div>
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={12}>
            <Button 
              type="text" 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBack}
              style={{ fontSize: '16px' }}
            />
            <Title level={3} style={{ margin: 0 }}>
              Sổ Quỹ - {dayjs(cashBookData.from_date).format('DD/MM/YYYY HH:mm')} đến {dayjs(cashBookData.to_date).format('DD/MM/YYYY HH:mm')}
            </Title>
          </Flex>
          <Space>
            {cashBookData.status === CashBookStatus.CLOSED && (
              <Button 
                type="default" 
                icon={<PrinterOutlined />}
                onClick={showPrintView}
                size="large"
              >
                In sổ quỹ
              </Button>
            )}
            {cashBookData.status === CashBookStatus.ACTIVE ? (
              <Button 
                type="primary" 
                danger 
                icon={<LockOutlined />}
                onClick={handleCloseCashBookClick}
                size="large"
              >
                Chốt sổ
              </Button>
            ) : (
              <Button 
                type="primary" 
                icon={<LockOutlined />}
                onClick={handleReopenCashBookClick}
                size="large"
              >
                Hủy chốt sổ
              </Button>
            )}
          </Space>
        </Flex>
      </Card>

      {/* Information Panels */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} md={12}>
          <Card title="Thông tin cơ bản" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Tên">{cashBookData.name}</Descriptions.Item>
                          <Descriptions.Item label="Khoảng thời gian">
              {dayjs(cashBookData.from_date).format('DD/MM/YYYY HH:mm')} - {dayjs(cashBookData.to_date).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
              <Descriptions.Item label="Trạng thái">
                <Tag color={cashBookData.status === CashBookStatus.ACTIVE ? 'green' : 'red'}>
                  {MAP_CASHBOOK_STATUS[cashBookData.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Người tạo">
                {cashBookData.created_by_name || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Thông tin chốt sổ" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Chênh lệch">
                {cashBookData.status === CashBookStatus.CLOSED 
                  ? (
                    <span style={{ 
                      color: (cashBookData.difference_amount || 0) >= 0 ? '#52c41a' : '#ff4d4f',
                      fontWeight: 'bold'
                    }}>
                      {formatCurrency(cashBookData.difference_amount || 0)}
                    </span>
                  )
                  : '-'
                }
              </Descriptions.Item>
              <Descriptions.Item label="Ngày tạo">
                {dayjs(cashBookData.created_at).format('DD/MM/YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày chốt">
                {cashBookData.closed_at ? dayjs(cashBookData.closed_at).format('DD/MM/YYYY HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Ghi chú chốt sổ">
                {cashBookData.notes || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderColor: '#d9d9d9', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Số dư đầu kỳ"
              value={cashBookData.opening_balance}
              valueStyle={{ color: '#1890ff', fontSize: '18px', fontWeight: 'bold' }}
              formatter={(value) => formatCurrency(Number(value))}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderColor: '#d9d9d9', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Số dư cuối kỳ"
              value={cashBookData.closing_balance || 0}
              valueStyle={{ color: '#52c41a', fontSize: '18px', fontWeight: 'bold' }}
              formatter={(value) => formatCurrency(Number(value))}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderColor: '#d9d9d9', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Tổng thu"
              value={cashBookData.total_income || 0}
              valueStyle={{ color: '#52c41a', fontSize: '18px', fontWeight: 'bold' }}
              formatter={(value) => formatCurrency(Number(value))}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderColor: '#d9d9d9', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Tổng chi"
              value={cashBookData.total_expense || 0}
              valueStyle={{ color: '#ff4d4f', fontSize: '18px', fontWeight: 'bold' }}
              formatter={(value) => formatCurrency(Number(value))}
            />
          </Card>
        </Col>
      </Row>

      {/* Transaction List */}
      <Card 
        title={
          <Flex justify="space-between" align="center">
            <span>Danh sách giao dịch ({cashBookData.receipts?.length || 0} giao dịch)</span>
            <Space>
              <Text>Chế độ xem:</Text>
              <Switch
                checked={showSeparatedView}
                onChange={setShowSeparatedView}
                checkedChildren="Chia tách"
                unCheckedChildren="Tổng hợp"
              />
              {cashBookData.status === CashBookStatus.ACTIVE && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddReceiptClick}
                >
                  Thêm thu chi
                </Button>
              )}
            </Space>
          </Flex>
        }
        size="small"
      >
        {showSeparatedView ? (
          // Chế độ chia tách - 2 bảng riêng
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Card 
                title={`Thu (${incomeReceipts.length} giao dịch)`}
                size="small"
                style={{ borderColor: '#52c41a' }}
              >
                <Table
                  columns={separatedColumns}
                  dataSource={incomeReceipts}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                  rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                />
              </Card>
            </div>
            <div>
              <Card 
                title={`Chi (${expenseReceipts.length} giao dịch)`}
                size="small"
                style={{ borderColor: '#ff4d4f' }}
              >
                <Table
                  columns={separatedColumns}
                  dataSource={expenseReceipts}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                  rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                />
              </Card>
            </div>
          </div>
        ) : (
          // Chế độ tổng hợp - 1 bảng chung
          <Table
            columns={transactionColumns}
            dataSource={cashBookData.receipts || []}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
          />
        )}
      </Card>

      <style dangerouslySetInnerHTML={{
        __html: `
          .table-row-light {
            background-color: #ffffff !important;
          }
          .table-row-dark {
            background-color: #fafafa !important;
          }
          .ant-table-thead > tr > th {
            background-color: #e6f7ff !important;
            border-bottom: 1px solid #1890ff !important;
          }
        `
      }} />

      {/* Close Cash Book Modal */}
      <Modal
        title="Chốt sổ quỹ"
        open={closeModalVisible}
        onCancel={() => setCloseModalVisible(false)}
        onOk={() => closeForm.submit()}
        confirmLoading={loading}
      >
        <Form
          form={closeForm}
          layout="vertical"
          onFinish={handleCloseCashBook}
        >
          <Form.Item
            label="Số dư cuối ngày"
            name="closing_balance"
            rules={[{ required: true, message: 'Vui lòng nhập số dư cuối ngày!' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={0}
              formatter={value => formatterInputNumber(value)}
              addonAfter="VNĐ"
              step={10000}
            />
          </Form.Item>
          <Form.Item
            label="Ghi chú"
            name="notes"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Reopen Cash Book Modal */}
      <Modal
        title="Hủy chốt sổ quỹ"
        open={reopenModalVisible}
        onCancel={() => setReopenModalVisible(false)}
        onOk={handleReopenCashBook}
        confirmLoading={loading}
        okText="Hủy chốt sổ"
        cancelText="Hủy"
      >
        <p>Bạn có chắc chắn muốn hủy chốt sổ quỹ này?</p>
        <p>Khi hủy chốt sổ, bạn có thể tiếp tục thêm giao dịch mới.</p>
      </Modal>

      <ReceiptForm
        visible={receiptFormVisible}
        mode={receiptFormMode}
        initialData={selectedReceipt || undefined}
        cashBookId={id || ''}
        onCancel={() => setReceiptFormVisible(false)}
        onSubmit={handleReceiptFormSubmit}
        loading={loading}
      />

      <Modal
        title="Xác nhận xóa thu chi"
        open={deleteReceiptModalVisible}
        onOk={handleDeleteReceipt}
        onCancel={() => {
          setDeleteReceiptModalVisible(false)
          setReceiptToDelete(null)
        }}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
      >
        <p>
          Bạn có chắc chắn muốn xóa giao dịch "{receiptToDelete?.name}" 
          ({formatCurrency(receiptToDelete?.amount || 0)})?
        </p>
        <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          Hành động này không thể hoàn tác!
        </p>
      </Modal>
    </div>
  )
}

export default Detail 